import { Edit as EditIcon } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { HorizBox, PadBox } from 'component/basics';
import { CTAButton, SubtleButton, TextButton } from 'component/button';
import { FormField } from 'component/form';
import { TextField, UtilityText } from 'component/text';
import React, { useEffect, useEffectEvent, useState } from 'react';
import { type TLabel, t } from 'util/stdtypes';

export type SaveableTextFieldProps = {
    label: TLabel;
    description?: React.JSX.Element;
    descriptionLabel?: TLabel;
    error?: React.JSX.Element;
    placeholder?: TLabel;
    value: string | null;
    multiline?: boolean;
    testID?: string;
    onSave: (value: string) => Promise<void> | void;
    onChange?: (value: string) => void;
    onCancel?: () => void;
    accessoryActionWhenEditing?: React.JSX.Element;
    required?: boolean;
    defaultIsEditing?: boolean;
    saveLabel?: TLabel;
    savingLabel?: TLabel;
    cancelLabel?: TLabel;
    disableSaveWhenEmpty?: boolean;
};

// Note that, unlike all other form elements, this one has the form field built
// it, since it contains an edit button. TODO: Possible refactor all fields to
// include their FormField headers.
export function SaveableTextField({
    label,
    value,
    onSave,
    onChange,
    onCancel,
    description,
    descriptionLabel,
    error,
    accessoryActionWhenEditing,
    multiline = false,
    testID,
    placeholder,
    required = false,
    defaultIsEditing = false,
    saveLabel,
    savingLabel,
    cancelLabel,
    disableSaveWhenEmpty = false,
}: SaveableTextFieldProps) {
    const { colors } = useTheme();
    const [isEditing, setIsEditing] = useState(defaultIsEditing);
    const [tempValue, setTempValue] = useState(value ?? null);
    const [isSaving, setIsSaving] = useState(false);

    const notifyChange = useEffectEvent((value: string) => {
        onChange?.(value);
    });
    useEffect(() => {
        notifyChange(tempValue ?? '');
    }, [tempValue]);

    useEffect(() => {
        if (!isEditing) {
            setTempValue(value ?? '');
        }
    }, [value, isEditing]);

    const isBlank = !tempValue?.trim();
    const saveDisabled = isSaving || (!!error && !tempValue) || (disableSaveWhenEmpty && isBlank);

    const editButton = (
        <SubtleButton icon={EditIcon} label={t('form.edit_button', 'Edit')} onPress={() => setIsEditing(true)} testID={`${testID}-edit`} />
    );

    return (
        <FormField accessoryAction={isEditing ? accessoryActionWhenEditing : editButton} label={label} required={required}>
            <TextField
                editable={isEditing}
                error={!!error}
                multiline={multiline}
                onChange={(value) => setTempValue(value)}
                placeholderLabel={placeholder}
                testID={testID}
                value={tempValue}
            />
            <TextFieldDescription description={description} descriptionLabel={descriptionLabel} error={isEditing ? error : undefined} />
            {isEditing && (
                <PadBox top={12}>
                    <HorizBox center gap={12} right>
                        <TextButton
                            color={colors.colorTextSecondary}
                            label={cancelLabel ?? t('form.cancel_button', 'Cancel')}
                            onPress={() => {
                                setTempValue(value);
                                setIsEditing(false);
                                onCancel?.();
                            }}
                            underline
                        />
                        <CTAButton
                            disabled={saveDisabled}
                            label={isSaving && savingLabel ? savingLabel : (saveLabel ?? t('form.save_button', 'Save'))}
                            onPress={async () => {
                                setIsSaving(true);
                                try {
                                    await onSave(tempValue ?? '');
                                    setIsEditing(false);
                                } catch (error) {
                                    // Error display handled by parent
                                    // component. Form should stay open.
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                        />
                    </HorizBox>
                </PadBox>
            )}
        </FormField>
    );
}

type TextFieldDescriptionProps = {
    description?: React.JSX.Element;
    descriptionLabel?: TLabel;
    error?: React.JSX.Element;
};

function TextFieldDescription({ description, descriptionLabel, error }: TextFieldDescriptionProps) {
    const { colors } = useTheme();
    return (
        <>
            {description && !error && <PadBox top={12}>{description}</PadBox>}
            {descriptionLabel && !error && (
                <PadBox top={12}>
                    <UtilityText color={colors.colorTextSecondary} label={descriptionLabel} type='small' />
                </PadBox>
            )}
            {error && <PadBox top={12}>{error}</PadBox>}
        </>
    );
}
